/* src/data/offersStore.ts */
import { create } from "zustand";
import { v4 as uuidv4 } from "uuid";
import { getApiUrl } from "@/utils/apiConfig";

export type PartnerType = "parti" | "media" | "ong" | "institution" | "sap" | "all";

export interface Offer {
  id: string;
  title: string;
  subtitle?: string; // optional short tagline
  body: string; // main markdown / html content
  target: PartnerType; // who can see the offer
  createdAt: string; // ISO date string
  status: "draft" | "published"; // draft until admin publishes
}

interface OffersStore {
  offers: Offer[];
  isLoaded: boolean;
  fetchOffers: () => Promise<void>;
  create: (offer: Omit<Offer, "id" | "createdAt" | "status">) => Promise<Offer>;
  update: (id: string, updates: Partial<Omit<Offer, "id" | "createdAt">>) => Promise<Offer | null>;
  delete: (id: string) => Promise<boolean>;
  publish: (id: string) => Promise<Offer | null>;
  getVisibleForPartner: (partnerType: PartnerType) => Offer[];
}

const API_URL = getApiUrl();

export const useOffersStore = create<OffersStore>((set, get) => ({
  offers: [],
  isLoaded: false,

  fetchOffers: async () => {
    try {
      const res = await fetch(`${API_URL}/offers.php?t=${Date.now()}`);
      if (!res.ok) throw new Error("API Error");
      const data = await res.json();
      if (Array.isArray(data)) {
        set({ offers: data, isLoaded: true });
      }
    } catch (e) {
      console.error("Failed to fetch offers", e);
    }
  },

  create: async (offer) => {
    const newOffer: Offer = {
      id: uuidv4(),
      createdAt: new Date().toISOString(),
      status: "draft",
      ...offer,
    };
    
    set((state) => ({ offers: [newOffer, ...state.offers] }));

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${API_URL}/offers.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'create', offer: newOffer })
      });
    } catch (e) { console.error("Failed to create offer", e); }

    return newOffer;
  },

  update: async (id, updates) => {
    const { offers } = get();
    const index = offers.findIndex(o => o.id === id);
    if (index === -1) return null;
    
    const updatedOffer = { ...offers[index], ...updates };
    const updatedOffers = [...offers];
    updatedOffers[index] = updatedOffer;
    
    set({ offers: updatedOffers });

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${API_URL}/offers.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'update', id, updates })
      });
    } catch (e) { console.error("Failed to update offer", e); }

    return updatedOffer;
  },

  delete: async (id) => {
    const { offers } = get();
    const filtered = offers.filter(o => o.id !== id);
    if (filtered.length === offers.length) return false;
    
    set({ offers: filtered });

    try {
      const token = localStorage.getItem('kaynin_auth_token');
      await fetch(`${API_URL}/offers.php`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify({ action: 'delete', id })
      });
    } catch (e) { console.error("Failed to delete offer", e); }

    return true;
  },

  publish: async (id) => {
    return await get().update(id, { status: "published" });
  },

  getVisibleForPartner: (partnerType) => {
    return get().offers.filter(o => o.status === "published" && (o.target === "all" || o.target === partnerType));
  }
}));
